import { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";

/**
 * A4 vertical event flyer for the upcoming Members Night.
 * Reads live from /public/next-event so the poster always shows the current
 * event or the auto-computed next Friday 22:00.
 */
export default function EventPoster({ testId = "poster-event" }) {
    const [ev, setEv] = useState(null);
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const url = `${origin}/join`;

    useEffect(() => {
        let alive = true;
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/next-event`)
            .then((r) => r.json())
            .then((d) => { if (alive) setEv(d); })
            .catch(() => {});
        return () => { alive = false; };
    }, []);

    if (!ev) return null;

    const d = new Date(ev.iso);
    const day = d.toLocaleDateString("en-GB", { weekday: "long", timeZone: "Europe/Stockholm" });
    const dayNum = d.toLocaleDateString("en-GB", { day: "2-digit", timeZone: "Europe/Stockholm" });
    const monthUpper = d.toLocaleDateString("en-GB", { month: "long", timeZone: "Europe/Stockholm" }).toUpperCase();
    const year = d.toLocaleDateString("en-GB", { year: "numeric", timeZone: "Europe/Stockholm" });
    const time = d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Stockholm" });

    return (
        <div
            className="poster relative w-full aspect-[1/1.414] rounded-md overflow-hidden bg-[#050A08] p-10 flex flex-col justify-between border border-gold/30"
            data-testid={testId}
        >
            {/* Atmospheric bg */}
            <div className="absolute inset-0 pointer-events-none">
                <div className="absolute inset-0 bg-[radial-gradient(80%_50%_at_50%_0%,rgba(212,175,55,0.18),transparent_60%)]" />
                <div className="absolute inset-0 bg-[radial-gradient(60%_40%_at_20%_100%,rgba(20,90,55,0.25),transparent_70%)]" />
                <div className="absolute inset-0 noise" />
            </div>
            {/* Corner brackets */}
            <div className="absolute top-6 left-6 w-6 h-6 border-t border-l border-gold/60" />
            <div className="absolute top-6 right-6 w-6 h-6 border-t border-r border-gold/60" />
            <div className="absolute bottom-6 left-6 w-6 h-6 border-b border-l border-gold/60" />
            <div className="absolute bottom-6 right-6 w-6 h-6 border-b border-r border-gold/60" />

            {/* Top — wordmark */}
            <div className="relative text-center">
                <div className="label-eyebrow !text-gold !tracking-[0.4em]">John Scotts · Kungsträdgården</div>
                <div className="font-serif font-medium text-4xl mt-2">
                    Scotts<span className="gold-text">Club</span>
                </div>
            </div>

            {/* Middle — headline */}
            <div className="relative text-center">
                <div className="label-eyebrow !text-ink-secondary mb-4">Presents</div>
                <div className="font-serif italic text-ink-primary text-5xl leading-none mb-6 px-4">
                    {ev.title || "Members Night"}
                </div>

                {/* Massive date block */}
                <div className="flex items-center justify-center gap-6 my-2">
                    <div className="text-right">
                        <div className="label-eyebrow !text-gold">{day}</div>
                        <div className="text-ink-secondary text-xs tracking-[0.28em] uppercase mt-1">{year}</div>
                    </div>
                    <div className="font-serif text-[7rem] leading-none gold-text tabular-nums" style={{ fontWeight: 500 }}>
                        {dayNum}
                    </div>
                    <div className="text-left">
                        <div className="label-eyebrow !text-gold">{monthUpper}</div>
                        <div className="text-ink-secondary text-xs tracking-[0.28em] uppercase mt-1">{time}</div>
                    </div>
                </div>

                <div className="gold-divider opacity-70 w-40 mx-auto my-6" />
                <div className="font-serif italic text-ink-secondary text-lg px-8">
                    {ev.subtitle || "John Scotts · Kungsträdgården"}
                </div>
            </div>

            {/* Bottom — QR + info */}
            <div className="relative flex items-end justify-between gap-6">
                <div className="text-left max-w-[45%]">
                    <div className="label-eyebrow !text-gold">Apply</div>
                    <div className="font-serif text-2xl text-ink-primary leading-tight mt-2">
                        Scan to join<br />
                        <span className="italic text-ink-secondary">the guestlist</span>
                    </div>
                    <div className="mt-4 text-ink-muted text-[10px] tracking-[0.24em] uppercase">
                        Hamngatan 19 · Stockholm<br />
                        Members only · By application
                    </div>
                </div>
                <div className="bg-white p-3 rounded-md">
                    <QRCodeSVG value={url} size={180} level="H" bgColor="#ffffff" fgColor="#050A08" includeMargin={false} />
                </div>
            </div>
        </div>
    );
}
